// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 08025af9-ed71-4271-b707-231b3dccf309

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, TextField, MenuItem, Alert,
  CircularProgress, Tooltip, IconButton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getConfig } from './config';
import { getIdToken } from './auth';

// Change history for one fact sheet (or for a whole tenant when factSheetId is
// omitted). Reads the audit_log written by the database triggers in
// backend/sql/005_audit.sql:
//   { id, at, actor, tenant_id, entity_type, entity_id, entity_name,
//     action: INSERT|UPDATE|DELETE, changes: { field: { from, to } } }
//
// The /history endpoint may not be deployed in every environment yet, so a 404
// or a network failure degrades to an informational note instead of an error —
// the tab must always render.

const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEADER = 'sectionHeader.main';

const ACTIONS = {
  INSERT: { label: 'created', fg: '#1B5E20', bg: '#E8F5E9', border: '#A5D6A7', dot: '#2E7D32' },
  UPDATE: { label: 'changed', fg: '#0D3C61', bg: '#E3F0FA', border: '#A9CDE8', dot: BLUE },
  DELETE: { label: 'deleted', fg: '#8E1414', bg: '#FDECEA', border: '#F1B0AB', dot: '#C62828' },
};
const actionMeta = (a) => ACTIONS[String(a || '').toUpperCase()]
  || { label: String(a || 'changed').toLowerCase(), fg: GREY, bg: '#EEF1F4', border: '#D5DCE3', dot: GREY };

// "2 hours ago" — small enough not to be worth a dependency.
function relativeTime(date) {
  if (!date || isNaN(date.getTime())) return '';
  const secs = Math.round((Date.now() - date.getTime()) / 1000);
  if (secs < 0) return 'just now';
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'} ago`;
  if (secs < 45) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return plural(Math.max(mins, 1), 'minute');
  const hours = Math.round(mins / 60);
  if (hours < 24) return plural(hours, 'hour');
  const days = Math.round(hours / 24);
  if (days < 31) return plural(days, 'day');
  const months = Math.round(days / 30.4);
  if (months < 12) return plural(months, 'month');
  return plural(Math.round(months / 12), 'year');
}

function truncate(text, max = 120) {
  const s = String(text);
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

// Values arrive as raw jsonb, so anything can show up here.
function formatValue(value) {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value.trim() === '' ? '—' : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    if (!value.length) return '—';
    return value.map((v) => (v && typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ');
  }
  try {
    return Object.entries(value).map(([k, v]) => `${k}: ${v && typeof v === 'object' ? JSON.stringify(v) : v}`).join(', ');
  } catch (e) {
    return String(value);
  }
}

// snake_case from the database, camelCase if the API ever maps it — accept both.
function normalize(raw, index) {
  const pick = (...keys) => {
    for (let i = 0; i < keys.length; i += 1) {
      if (raw[keys[i]] !== undefined && raw[keys[i]] !== null) return raw[keys[i]];
    }
    return null;
  };
  let changes = pick('changes', 'diff');
  if (typeof changes === 'string') {
    try { changes = JSON.parse(changes); } catch (e) { changes = {}; }
  }
  if (!changes || typeof changes !== 'object' || Array.isArray(changes)) changes = {};
  const at = pick('at', 'changedAt', 'timestamp', 'created_at');
  return {
    key: String(pick('id', 'auditId') || `row-${index}`),
    at: at ? new Date(at) : null,
    actor: pick('actor', 'actorEmail', 'user_email') || 'system',
    entityType: pick('entity_type', 'entityType') || '',
    entityName: pick('entity_name', 'entityName') || '',
    action: String(pick('action', 'op') || 'UPDATE').toUpperCase(),
    changes,
  };
}

export default function HistoryTab({ factSheetId, tenantId }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState('');
  const [actor, setActor] = useState('');
  const [fieldQuery, setFieldQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setUnavailable('');
    const qs = factSheetId
      ? `entityId=${encodeURIComponent(factSheetId)}`
      : (tenantId ? `tenantId=${encodeURIComponent(tenantId)}` : '');
    try {
      const res = await fetch(`${getConfig().apiUrl}/history${qs ? `?${qs}` : ''}`, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getIdToken()}` },
      });
      if (!res.ok) {
        setRows([]);
        setUnavailable(res.status === 404
          ? 'The change-history API is not deployed in this environment yet. Once the /history endpoint is live, every create, update and delete recorded by the audit trail will appear here.'
          : `The change-history API could not be reached (${res.status}). History will appear here once the /history endpoint is available.`);
        return;
      }
      const text = await res.text();
      // An unparseable body is the API being unreachable in practice; saying
      // so beats rendering an empty history as though it were the truth.
      let data = [];
      try { data = text ? JSON.parse(text) : []; } catch (e) {
        setUnavailable('The change-history API returned a response that could not be read as JSON. History will appear here once the /history endpoint returns valid data.');
        return;
      }
      const list = Array.isArray(data) ? data : (Array.isArray(data.items) ? data.items : []);
      setRows(list.map(normalize));
      // The API caps the page. Without the true total the chip read
      // "12 of 200 entries" and a client concluded that was their whole
      // history. An older array-shaped response has no total, in which case
      // treat the page as complete rather than inventing a number.
      setTotal(Array.isArray(data) ? list.length : (data.total ?? list.length));
    } catch (e) {
      setRows([]);
      setUnavailable('The change-history API is not deployed in this environment yet, so no audit trail could be loaded. This tab will fill in automatically once the /history endpoint is live.');
    } finally {
      setLoading(false);
    }
  }, [factSheetId, tenantId]);

  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(
    () => rows.slice().sort((a, b) => (b.at ? b.at.getTime() : 0) - (a.at ? a.at.getTime() : 0)),
    [rows],
  );

  const actors = useMemo(() => {
    const seen = {};
    sorted.forEach((r) => { if (r.actor) seen[r.actor] = true; });
    return Object.keys(seen).sort();
  }, [sorted]);

  // A field filter narrows both which entries show and which rows inside them.
  const entries = useMemo(() => {
    const needle = fieldQuery.trim().toLowerCase();
    const out = [];
    sorted.forEach((r) => {
      if (actor && r.actor !== actor) return;
      const fields = Object.keys(r.changes)
        .filter((f) => !needle || f.toLowerCase().includes(needle))
        .sort();
      if (needle && !fields.length) return;
      out.push({ ...r, fields });
    });
    return out;
  }, [sorted, actor, fieldQuery]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }} useFlexGap flexWrap="wrap">
        <Typography variant="h6" sx={{ fontWeight: 700, color: HEADER }}>
          {factSheetId ? 'Change history' : 'Change history — all fact sheets'}
        </Typography>
        {!loading && !unavailable && (
          <Chip size="small"
            label={total > sorted.length
              // Say plainly that this is a window onto a longer trail rather
              // than counting against the page and implying it is everything.
              ? `${entries.length} shown · ${sorted.length} loaded of ${total} total`
              : `${entries.length} of ${sorted.length} ${sorted.length === 1 ? 'entry' : 'entries'}`}
            sx={{ height: 20, bgcolor: '#EEF1F4', color: GREY }} />
        )}
        <Box sx={{ flex: 1 }} />
        <TextField select size="small" label="Actor" value={actor} onChange={(e) => setActor(e.target.value)}
          sx={{ minWidth: 200 }}>
          <MenuItem value="">All actors</MenuItem>
          {actors.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Field name" placeholder="e.g. owner" value={fieldQuery}
          onChange={(e) => setFieldQuery(e.target.value)} sx={{ minWidth: 180 }} />
        <Tooltip title="Reload history">
          <IconButton onClick={load}><RefreshIcon /></IconButton>
        </Tooltip>
      </Stack>

      {unavailable && <Alert severity="info" sx={{ mb: 2 }}>{unavailable}</Alert>}

      <Paper variant="outlined" sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>
        ) : !entries.length ? (
          <Typography sx={{ color: '#9AA6B2', textAlign: 'center', py: 4 }}>
            {sorted.length ? 'No changes match these filters.' : 'No changes recorded yet.'}
          </Typography>
        ) : (
          <Box sx={{ position: 'relative', pl: 3 }}>
            {/* the spine of the timeline */}
            <Box sx={{ position: 'absolute', left: 7, top: 6, bottom: 6, width: '2px', bgcolor: '#E1E7EC' }} />
            {entries.map((entry) => {
              const meta = actionMeta(entry.action);
              const absolute = entry.at && !isNaN(entry.at.getTime()) ? entry.at.toLocaleString() : 'unknown time';
              return (
                <Box key={entry.key} sx={{ position: 'relative', pb: 2.5 }}>
                  <Box sx={{
                    position: 'absolute', left: -21, top: 5, width: 12, height: 12, borderRadius: '50%',
                    bgcolor: meta.dot, border: '2px solid #fff', boxShadow: `0 0 0 2px ${meta.border}`,
                  }} />
                  <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
                    <Tooltip title={absolute}>
                      <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: HEADER, cursor: 'default' }}>
                        {entry.at ? relativeTime(entry.at) : 'unknown time'}
                      </Typography>
                    </Tooltip>
                    <Chip size="small" label={meta.label}
                      sx={{ height: 20, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                        color: meta.fg, bgcolor: meta.bg, border: `1px solid ${meta.border}` }} />
                    <Typography component="span" sx={{ fontSize: 13, color: GREY }}>
                      by <Box component="b" sx={{ color: HEADER }}>{entry.actor}</Box>
                    </Typography>
                    {!factSheetId && (entry.entityName || entry.entityType) && (
                      <Typography component="span" sx={{ fontSize: 12, color: GREY }}>
                        · {entry.entityName || entry.entityType}
                        {entry.entityName && entry.entityType ? ` (${entry.entityType})` : ''}
                      </Typography>
                    )}
                  </Stack>

                  {entry.fields.length > 0 && (
                    <Box sx={{ mt: 0.75, borderLeft: `3px solid ${meta.border}`, pl: 1.25 }}>
                      {entry.fields.map((field) => {
                        const change = entry.changes[field] || {};
                        const from = truncate(formatValue(change.from));
                        const to = truncate(formatValue(change.to));
                        return (
                          <Box key={field} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 0.75, py: 0.25 }}>
                            <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 700, color: HEADER }}>
                              {field}:
                            </Typography>
                            <Tooltip title={formatValue(change.from)}>
                              <Typography component="span" sx={{ fontSize: 12.5, color: GREY, textDecoration: 'line-through', cursor: 'default' }}>
                                {from}
                              </Typography>
                            </Tooltip>
                            <Typography component="span" sx={{ fontSize: 12.5, color: BLUE, fontWeight: 700 }}>→</Typography>
                            <Tooltip title={formatValue(change.to)}>
                              <Typography component="span" sx={{ fontSize: 12.5, color: HEADER, cursor: 'default' }}>
                                {to}
                              </Typography>
                            </Tooltip>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
